import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Hash, Send, RefreshCw, MessageSquare, ChevronDown, ChevronRight } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DispatchPage() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [expandedThreads, setExpandedThreads] = useState({});
  const [threadReplies, setThreadReplies] = useState({});
  const [loadingThreads, setLoadingThreads] = useState({});
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchMessages();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchMessages, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchMessages = async () => {
    try {
      const token = localStorage.getItem('vaultai_token');
      const res = await axios.get(`${API}/slack/dispatch?limit=40`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        setMessages(res.data.messages || []);
      }
    } catch (err) {
      console.error('Failed to fetch dispatch messages:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const fetchThreadReplies = async (threadTs) => {
    if (threadReplies[threadTs]) {
      // Already loaded, just toggle visibility
      setExpandedThreads(prev => ({ ...prev, [threadTs]: !prev[threadTs] }));
      return;
    }

    setLoadingThreads(prev => ({ ...prev, [threadTs]: true }));
    try {
      const token = localStorage.getItem('vaultai_token');
      const res = await axios.get(`${API}/slack/thread/${threadTs}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        setThreadReplies(prev => ({ ...prev, [threadTs]: res.data.replies }));
        setExpandedThreads(prev => ({ ...prev, [threadTs]: true }));
      } else {
        toast.error('Failed to load replies');
      }
    } catch (err) {
      console.error('Failed to fetch thread replies:', err);
      toast.error('Failed to load replies');
    } finally {
      setLoadingThreads(prev => ({ ...prev, [threadTs]: false }));
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchMessages();
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    setSending(true);
    try {
      const token = localStorage.getItem('vaultai_token');
      const res = await axios.post(`${API}/slack/send`, 
        { message: newMessage },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (res.data.success) {
        setNewMessage('');
        toast.success('Message sent to #dispatch');
        // Refresh to show new message
        setTimeout(fetchMessages, 1000);
      } else {
        toast.error('Failed to send message');
      }
    } catch (err) {
      console.error('Send failed:', err);
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(parseFloat(timestamp) * 1000);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const formatFullTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(parseFloat(timestamp) * 1000);
    return date.toLocaleString();
  };

  // Clean up Slack message formatting
  const cleanMessage = (text) => {
    if (!text) return '';
    return text
      .replace(/<#[A-Z0-9]+\|([^>]+)>/g, '#$1') // Replace channel mentions
      .replace(/<([^|>]+)\|([^>]+)>/g, '$2') // Replace links with label
      .replace(/<(https?:\/\/[^>]+)>/g, '$1') // Replace URL links
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dispatch-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Hash className="w-6 h-6 text-purple-400" />
            Dispatch
          </h1>
          <p className="text-muted-foreground">Real-time messages from Slack #dispatch channel</p>
        </div>
        <Button variant="outline" onClick={handleRefresh} disabled={refreshing}>
          <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Messages */}
      <Card className="border-purple-500/20">
        <CardHeader className="pb-3 border-b border-border">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-purple-400" />
              #dispatch
            </CardTitle>
            <span className="text-sm text-muted-foreground">
              {messages.length} messages • Latest first • Auto-refresh 30s
            </span>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {/* Message List */}
          <div className="h-[calc(100vh-280px)] overflow-y-auto p-4 space-y-4">
            {messages.length > 0 ? (
              messages.map((msg) => {
                // Skip system messages
                if (msg.text?.includes('has joined the channel')) return null;
                
                const hasReplies = msg.reply_count > 0;
                const threadTs = msg.id; // Use message ts as thread_ts for parent messages
                const isExpanded = expandedThreads[threadTs];
                const replies = threadReplies[threadTs] || [];
                const isLoadingReplies = loadingThreads[threadTs];
                
                return (
                  <div key={msg.id} className="group">
                    {/* Main Message */}
                    <div className="flex gap-3">
                      <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                        {msg.avatar ? (
                          <img src={msg.avatar} alt="" className="w-10 h-10 rounded-lg" />
                        ) : (
                          <MessageSquare className="w-5 h-5 text-purple-400" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold text-foreground">{msg.user || 'Unknown'}</span>
                          <span className="text-xs text-muted-foreground" title={formatFullTime(msg.timestamp)}>
                            {formatTime(msg.timestamp)}
                          </span>
                        </div>
                        <p className="text-sm text-foreground/90 whitespace-pre-wrap break-words">
                          {cleanMessage(msg.text)}
                        </p>
                        
                        {/* Reply Count - Clickable */}
                        {hasReplies && (
                          <button
                            onClick={() => fetchThreadReplies(threadTs)}
                            className="flex items-center gap-1 mt-2 text-xs text-purple-400 hover:text-purple-300 transition-colors"
                            disabled={isLoadingReplies}
                          >
                            {isLoadingReplies ? (
                              <div className="w-3 h-3 border border-purple-400 border-t-transparent rounded-full animate-spin" />
                            ) : isExpanded ? (
                              <ChevronDown className="w-3 h-3" />
                            ) : (
                              <ChevronRight className="w-3 h-3" />
                            )}
                            {msg.reply_count} {msg.reply_count === 1 ? 'reply' : 'replies'}
                            {!isExpanded && ' - click to view'}
                          </button>
                        )}
                      </div>
                    </div>
                    
                    {/* Thread Replies */}
                    {isExpanded && replies.length > 0 && (
                      <div className="ml-6 mt-2 pl-6 border-l-2 border-purple-500/30 space-y-3">
                        {replies.map((reply) => (
                          <div key={reply.id} className="flex gap-3">
                            <div className="w-8 h-8 rounded-lg bg-purple-500/10 flex items-center justify-center flex-shrink-0">
                              {reply.avatar ? (
                                <img src={reply.avatar} alt="" className="w-8 h-8 rounded-lg" />
                              ) : (
                                <MessageSquare className="w-4 h-4 text-purple-400/70" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-medium text-sm text-foreground">{reply.user || 'Unknown'}</span>
                                <span className="text-xs text-muted-foreground" title={formatFullTime(reply.timestamp)}>
                                  {formatTime(reply.timestamp)}
                                </span>
                              </div>
                              <p className="text-sm text-foreground/80 whitespace-pre-wrap break-words">
                                {cleanMessage(reply.text)}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <MessageSquare className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No messages yet</p>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Send Message */}
          <form onSubmit={handleSendMessage} className="border-t border-border p-4">
            <div className="flex gap-2">
              <Input
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Send a message to #dispatch..."
                className="flex-1"
                disabled={sending}
              />
              <Button type="submit" disabled={sending || !newMessage.trim()}>
                <Send className="w-4 h-4 mr-2" />
                {sending ? 'Sending...' : 'Send'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
